import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatIDR, formatDateTime } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MapPin, CalendarBlank, ArrowRight, Lightning } from "@phosphor-icons/react";

function EventCard({ ev, featured }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={featured ? "md:col-span-8 md:row-span-2" : "md:col-span-4"}
    >
      <Link
        to={`/events/${ev.id}`}
        data-testid={`event-card-${ev.id}`}
        className="group block h-full bg-card border border-white/10 overflow-hidden hover:-translate-y-1 hover:neon-glow transition-all duration-300"
      >
        <div className={`relative overflow-hidden ${featured ? "h-72 md:h-[26rem]" : "h-48"}`}>
          <img src={ev.poster_url} alt={ev.name} onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1200&q=80"; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <span className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-primary text-black px-2.5 py-1">
            {ev.category}
          </span>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className={`font-heading font-black text-white leading-none tracking-tight ${featured ? "text-3xl md:text-5xl" : "text-xl"}`}>
              {ev.name}
            </h3>
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-4 text-sm text-zinc-400 mb-3">
            <span className="flex items-center gap-1.5"><CalendarBlank size={15} className="text-primary" /> {formatDateTime(ev.date)}</span>
          </div>
          <p className="flex items-center gap-1.5 text-sm text-zinc-400 mb-4">
            <MapPin size={15} className="text-primary" /> {ev.location}
          </p>
          {featured && <p className="text-zinc-500 text-sm mb-4 line-clamp-2">{ev.description}</p>}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Mulai dari</p>
              <p className="font-heading font-bold text-primary text-lg">{formatIDR(ev.min_price)}</p>
            </div>
            <span className="flex items-center gap-1 text-sm font-semibold text-white group-hover:text-primary transition-colors">
              Beli Tiket <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">
              <Lightning weight="fill" size={16} /> Platform Tiket Acara
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.9] max-w-4xl">
              Amankan <span className="text-primary">tiketmu</span>,<br />rasakan pengalamannya.
            </h1>
            <p className="text-zinc-400 text-lg max-w-xl mt-6 leading-relaxed">
              Beli tiket nobar, musik, konser, konferensi, dan pameran favoritmu. E-Ticket dengan QR Code unik, langsung masuk tanpa antre.
            </p>
          </motion.div>
        </div>
        <div className="absolute -right-40 -top-40 w-96 h-96 bg-primary/10 blur-3xl rounded-full" />
        <div className="absolute -left-40 bottom-0 w-80 h-80 bg-secondary/10 blur-3xl rounded-full" />
      </section>

      {/* Catalog */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Acara Mendatang</h2>
          <span className="text-sm text-zinc-500">{events.length} acara</span>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-12 gap-6">
            {[0, 1, 2].map((i) => <div key={i} className="md:col-span-4 h-72 bg-card border border-white/10 animate-pulse" />)}
          </div>
        ) : events.length === 0 ? (
          <p className="text-zinc-500 py-20 text-center">Belum ada acara tersedia.</p>
        ) : (
          <div className="grid md:grid-cols-12 gap-6 auto-rows-min" data-testid="events-grid">
            {events.map((ev, i) => <EventCard key={ev.id} ev={ev} featured={i === 0} />)}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
